import type { Language } from '../../../common/types/i18n.js'
import type { FilterOption, FilterOptions } from './types.js'

/** Localized country name for an ISO 3166-1 alpha-2 code, falling back to the code. */
export const regionName = (code: string, lang: string): string => {
    try {
        return new Intl.DisplayNames([lang], { type: 'region' }).of(code) ?? code
    } catch {
        return code
    }
}

export interface EmployeeFilterRefs {
    companies: { id: number; name: string }[]
    teams: { id: number; name: string }[]
    locations: { id: number; name: string; country_code: string }[]
    managers: { id: number; first_name: string; last_name: string }[]
}

/**
 * Build the `FilterOptions` for the employee Filter popover from raw reference
 * data. Shared by the People directory and the Who's away calendar.
 */
export const buildEmployeeFilterOptions = (
    lang: Language,
    { companies, teams, locations, managers }: EmployeeFilterRefs,
): FilterOptions => {
    const countries: FilterOption[] = Array.from(
        new Set(locations.map((l) => l.country_code).filter(Boolean)),
    )
        .map((code) => ({ value: code, label: regionName(code, lang) }))
        .sort((a, b) => a.label.localeCompare(b.label))

    return {
        companies: companies.map((c) => ({ value: String(c.id), label: c.name })),
        teams: teams.map((tm) => ({ value: String(tm.id), label: tm.name })),
        locations: locations.map((l) => ({ value: String(l.id), label: l.name })),
        countries,
        managers: managers.map((m) => ({
            value: String(m.id),
            label: `${m.first_name} ${m.last_name}`,
        })),
    }
}
